import { useTableId } from "@/components/mapping/hooks";
import { AutoTagProgressModal } from "@/components/modals/auto-tag-progress-modal";
import {
  hasAutoTagRecord,
  isAutoTagRunning,
  useAutoTagStatus,
} from "@/hooks/mutations/use-auto-tag-status";
import { useAutoTagToolCalls } from "@/hooks/mutations/use-auto-tag-tool-calls";
import { useWorkbookId } from "@/hooks/use-workbook";
import { isDebugEnabled } from "@/lib/is-debug-enabled";
import { cn } from "@/lib/utils";
import { pushModal } from "@/stores/modal";
import SidebarPanel from "@/toggle-ui-kit/sidebar-panel";
import { Loader2, Sparkles } from "lucide-react";
import { AutoTagToolCallItem } from "./auto-tag-tool-call-item";
import {
  foldDisplayState,
  resolveDisplayState,
  STATE_CHIP_CLASS,
  STATE_LABELS,
  toolLabel,
} from "./constants";

/**
 * 오토태깅 상태 패널.
 * - 상단: 워크북 **전체** 진행(몇 / 몇 완료 + 성공/0개/실패) — 어느 표에 있든 보인다.
 * - 하단: **지금 보고 있는 표**의 상세(상태/현재 tool + tool 호출 로그) — 그 표 기록이 있을 때만.
 *
 * status 응답은 미실행 표도 포함하므로, 노출 여부와 진행률은 시도된 표 기준으로 계산한다.
 */
export const AutoTagProgressPanel = () => {
  const debug = isDebugEnabled();
  const { data: workbookId } = useWorkbookId();
  const {
    data: { tableId },
  } = useTableId();
  const { data: status } = useAutoTagStatus(workbookId ?? undefined);

  const tables = status?.tables ?? [];
  const attempted = tables.filter(hasAutoTagRecord);
  const found = tables.find((t) => t.tableId === tableId);
  const current = found && hasAutoTagRecord(found) ? found : undefined;
  const isRunning = current ? isAutoTagRunning(current) : false;

  // tool 호출 로그는 debug 모드에서만 노출하므로, 폴링도 debug 일 때만 돈다.
  const { data: toolCalls, isLoading } = useAutoTagToolCalls(
    workbookId ?? undefined,
    tableId ?? "",
    { enabled: debug && !!current && !!tableId, isRunning },
  );

  if (!workbookId || attempted.length === 0) return null;

  const currentState = current
    ? foldDisplayState(
        resolveDisplayState(current.status, current.outcome),
        debug,
      )
    : null;

  return (
    <SidebarPanel
      icon={<Sparkles className="size-4" strokeWidth={0.25} />}
      label="오토태깅 진행상황"
    >
      <div className="flex flex-col px-1">
        {/* 전체 진행 */}
        <div className="flex flex-col gap-1"></div>

        {/* 현재 표 상세 */}
        {current && currentState && (
          <div className="flex flex-col gap-2 ">
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-xs font-medium text-neutral-600">
                현재 표
              </span>
              <span
                className={cn(
                  "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                  STATE_CHIP_CLASS[currentState],
                )}
              >
                {isRunning && <Loader2 className="size-3 animate-spin" />}
                {STATE_LABELS[currentState]}
              </span>
              {isRunning && (
                <span className="truncate text-xs text-blue-500">
                  {toolLabel(current.currentTool)}
                </span>
              )}
              {debug && current.toolCallCount > 0 && (
                <span className="shrink-0 text-xs text-neutral-400">
                  {current.toolCallCount} 호출
                </span>
              )}
              <button
                type="button"
                onClick={() => pushModal(AutoTagProgressModal)}
                className="ml-auto text-xs text-blue-600 hover:underline"
              >
                전체 표 진행상황 보기
              </button>
            </div>
            {/* 에러: 일반 모드는 발생 여부만, debug 는 상세 메시지 */}
            {current.errorMessage &&
              (debug ? (
                <p className="rounded bg-red-50 p-1.5 text-xs text-red-600">
                  {current.errorMessage}
                </p>
              ) : (
                <p className="text-xs text-red-600">
                  오토태깅 중 에러가 발생했어요.
                </p>
              ))}
            {/* tool 호출 로그는 debug 전용 */}
            {debug && (
              <div className="flex flex-col gap-1">
                {toolCalls && toolCalls.calls.length > 0 ? (
                  toolCalls.calls.map((call) => (
                    <AutoTagToolCallItem key={call.seq} call={call} />
                  ))
                ) : isLoading ? (
                  <p className="flex items-center gap-1.5 px-1 py-1.5 text-xs text-neutral-400">
                    <Loader2 className="size-3 animate-spin" />
                    불러오는 중…
                  </p>
                ) : (
                  <p className="px-1 py-1.5 text-xs text-neutral-400">
                    {isRunning
                      ? "아직 호출이 없습니다."
                      : "호출 도구 목록이 없습니다."}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </SidebarPanel>
  );
};
